import { Box, Button, Card, Checkbox, Flex, Heading, IconButton, Table, Text } from '@chakra-ui/react';
import { ScheduledAvailabilityLimitationItemFragment, Service } from '@client/graphql/types/graphql.ts';
import AvailabilityScheduledCreateBatch from '@client/page/management/availability-limitation/component/availability-scheduled-create-batch.tsx';
import AvailabilityScheduledCreate from '@client/page/management/availability-limitation/component/availability-scheduled-create.tsx';
import AvailabilityScheduledUpdate from '@client/page/management/availability-limitation/component/availability-scheduled-updated.tsx';
import { ArrowPathIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import { useDialog } from '@packages/ui/modal/use-dialog';
import { useNotificationCenter } from '@packages/ui/notification/notification-center';
import { useTranslate } from '@tolgee/react';
import { capitalize } from 'lodash';
import { useState } from 'react';
import useDeleteSchedule from '../logic/use-delete-schedule';

export default function AvailabilityLimitationScheduledList(props: {
  service: Service;
  data: ScheduledAvailabilityLimitationItemFragment[];
  refetch: () => void;
}) {
  const { t } = useTranslate();
  const dialogManager = useModalManagerContext();
  const [deleteSchedule, { loading: deleting }] = useDeleteSchedule();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const [selection, setSelection] = useState<string[]>([]);

  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < props.data.length;

  const createModal = () => {
    dialogManager.showDialog((onClose) => <AvailabilityScheduledCreate service={props.service} onClose={onClose} />);
  };

  const updateModal = (data: ScheduledAvailabilityLimitationItemFragment) => {
    dialogManager.showDialog((onClose) => (
      <AvailabilityScheduledUpdate service={props.service} data={data} onClose={onClose} />
    ));
  };

  const handleBatchDelete = () => {
    dialog.destructAlert({
      title: t('common.action.delete'),
      message: t('alert.delete.confirm', 'Are you sure you want to delete the selected items?'),
      textButton: t('common.button.delete'),
      onConfirm: async () => {
        try {
          await Promise.all(selection.map((item) => deleteSchedule({ variables: { id: item } })));
          showAlert({
            status: 'success',
            title: t('common.action.delete'),
            message: t('alert.delete.success', 'Deleted successfully!'),
          });

          setSelection([]);
          props.refetch();
        } catch {
          showAlert({
            status: 'error',
            title: t('common.action.delete'),
            message: t('alert.delete.failed', 'Delete failed!'),
          });
        }
      },
    });
  };

  const createBatchModal = () => {
    dialogManager.showDialog((onClose) => (
      <AvailabilityScheduledCreateBatch service={props.service} onClose={onClose} />
    ));
  };

  const sortedDataByStartTime = props.data.slice().sort((a, b) => {
    const timeA = a.timeRule.startTime;
    const timeB = b.timeRule.startTime;
    return timeA.localeCompare(timeB);
  });

  const rows = sortedDataByStartTime.map((schedule) => (
    <Table.Row
      borderBottom={'1px solid'}
      borderBottomColor={'gray.100'}
      key={schedule.id}
      cursor="pointer"
      _hover={{ bg: 'gray.50' }}
    >
      <Table.Cell>
        <Checkbox.Root
          size="sm"
          variant={'subtle'}
          top="0.5"
          aria-label="Select row"
          checked={selection.includes(schedule.id)}
          onCheckedChange={(changes) => {
            setSelection((prev) =>
              changes.checked ? [...prev, schedule.id] : selection.filter((name) => name !== schedule.id),
            );
          }}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
        </Checkbox.Root>
      </Table.Cell>

      <Table.Cell
        onClick={() => {
          updateModal(schedule);
        }}
        py={4}
        textAlign="left"
      >
        <Flex flexWrap="wrap" gap={2}>
          <Text fontWeight="normal" letterSpacing="wide">
            {schedule.timeRule.daysOfWeeks.map((item) => capitalize(item).slice(0, 3)).join(', ')}
          </Text>
        </Flex>
      </Table.Cell>

      <Table.Cell
        onClick={() => {
          updateModal(schedule);
        }}
        textAlign="center"
      >
        <Flex direction={{ base: 'column', lg: 'row' }} align="center" justify="center" gap={2} flexWrap="wrap">
          <Text>{schedule.timeRule.startTime}</Text>
          <ArrowRightIcon className="hidden w-3 h-3 lg:inline-block" />
          <Text>{schedule.timeRule.endTime}</Text>
        </Flex>
      </Table.Cell>
      <Table.Cell
        onClick={() => {
          updateModal(schedule);
        }}
        textAlign="center"
        display={{ base: 'none', md: 'table-cell' }}
      >
        <Box
          fontWeight="normal"
          letterSpacing="wide"
          maxW="240px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          color="gray.600"
          mx="auto"
        >
          {schedule.message}
        </Box>
      </Table.Cell>
      <Table.Cell
        onClick={() => {
          updateModal(schedule);
        }}
        textAlign="right"
      >
        <Text fontWeight="normal" letterSpacing="wide">
          {schedule.capacity}
        </Text>
      </Table.Cell>
    </Table.Row>
  ));

  return (
    <>
      <Card.Root>
        <Card.Header py={4}>
          <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
            <Heading size="3xl" fontWeight={'bold'}>
              <Text fontSize={'2xl'}> {t('availability-limitation-scheduled-list.title')}</Text>
            </Heading>

            <Flex gap={2}>
              <Button onClick={createModal} bgColor="gray.800" color={'white'} p={2}>
                {t('common.button.create')}
              </Button>

              {props.service == Service.NailAppointment && (
                <Button
                  onClick={createBatchModal}
                  bgColor="gray.100"
                  p={2}
                  border={'1px solid'}
                  borderColor={'gray.200'}
                >
                  {t('common.button.bat-create', 'Batch create')}
                </Button>
              )}

              {selection.length > 0 && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBatchDelete();
                  }}
                  bgColor="red.500"
                  color={'white'}
                  p={2}
                  border={'1px solid'}
                  borderColor={'gray.200'}
                  disabled={deleting}
                >
                  {t('common.button.delete-batch', 'Delete batch')}
                </Button>
              )}

              <IconButton onClick={props.refetch} variant="outline" aria-label="Refresh">
                <ArrowPathIcon style={{ width: '20px', height: '20px' }} />
              </IconButton>
            </Flex>
          </Flex>
        </Card.Header>

        <Card.Body p={0}>
          <Table.Root size="md" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="6">
                  <Checkbox.Root
                    size="sm"
                    top="0.5"
                    aria-label="Select all rows"
                    checked={indeterminate ? 'indeterminate' : selection.length > 0}
                    onCheckedChange={(changes) => {
                      setSelection(changes.checked ? props.data.map((item) => item.id) : []);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="left">
                  {t('availability-limitation-scheduled-list.table-header-column.period')}
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="center">
                  {t('availability-limitation-scheduled-list.table-header-column.during')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  textAlign="center"
                  fontWeight={'semibold'}
                  display={{ base: 'none', md: 'table-cell' }}
                >
                  {t('availability-limitation-temporary-list.table-header-column.note')}
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight={'semibold'} textAlign="right">
                  {t('availability-limitation-scheduled-list.table-header-column.capacity')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>{rows}</Table.Body>
          </Table.Root>
        </Card.Body>
      </Card.Root>
    </>
  );
}
